// TrailerMode.js - Seed fake flights for trailer capture
// Toggle this flag to enable trailer mode
export const TRAILER_MODE = true;
export const TRAILER_FLIGHT_COUNT = 175; // 150-200 for trailer capture

import { AIRPORTS } from './AirportDatabase.js';
import { AIRCRAFT_DATABASE } from '../data/aircraft.js';
import { debug } from '../lib/debug.js';

// Aircraft types to use for trailer (mix of sizes) - must match AIRCRAFT_DATABASE keys
const TRAILER_AIRCRAFT = [
  'E175',        // Regional jet
  'E190',        // Regional jet
  'CRJ900',      // Regional jet
  'B737-800',    // Narrow body
  'B737MAX8',    // Narrow body
  'A320',        // Narrow body
  'A320neo',     // Narrow body
  'A321',        // Narrow body
  'B787-9',      // Wide body
  'B787-10',     // Wide body
  'A350-900',    // Wide body
  'A350-1000',   // Wide body
  'B777-300ER',  // Wide body
];

// Progress distribution for visual variety
const PROGRESS_DISTRIBUTION = [
  0.05, 0.08, 0.10, 0.12, 0.15, 0.18, 0.20, 0.22, 0.25, 0.28,
  0.30, 0.32, 0.35, 0.38, 0.40, 0.42, 0.45, 0.48, 0.50, 0.52,
  0.55, 0.58, 0.60, 0.62, 0.65, 0.68, 0.70, 0.72, 0.75, 0.78,
  0.80, 0.82, 0.85, 0.88, 0.90, 0.92, 0.95,
];

/**
 * Generate a random flight number
 */
function generateFlightNumber(index) {
  const prefixes = ['DG', 'AA', 'UA', 'DL', 'SW', 'AS', 'JB', 'NK', 'F9', 'HA'];
  const prefix = prefixes[index % prefixes.length];
  const number = 100 + Math.floor(Math.random() * 900);
  return `${prefix}${number}`;
}

/**
 * Get random item from array
 */
function randomItem(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Calculate distance between two airports (haversine)
 */
function calculateDistanceNm(origin, destination) {
  const R = 3440.065; // Earth radius in nautical miles
  const lat1 = origin.lat * Math.PI / 180;
  const lat2 = destination.lat * Math.PI / 180;
  const dLat = (destination.lat - origin.lat) * Math.PI / 180;
  const dLon = (destination.lon - origin.lon) * Math.PI / 180;

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Get phase from distance fraction (matches GameLoopEngine)
 */
function getPhaseFromProgress(fraction) {
  if (fraction < 0.08) return "takeoff";
  if (fraction < 0.20) return "climb";
  if (fraction < 0.80) return "cruise";
  if (fraction < 0.92) return "descent";
  if (fraction < 0.97) return "approach";
  if (fraction < 0.995) return "landing";
  return "taxi_in";
}

/**
 * Generate trailer flights - call this to seed fake flights
 */
export function generateTrailerFlights(gameTime = new Date()) {
  const airportCodes = Object.keys(AIRPORTS);
  const flights = [];

  // Filter to airports with valid coordinates (must be real numbers in valid ranges)
  const validAirports = airportCodes.filter(code => {
    const apt = AIRPORTS[code];
    if (!apt) return false;
    if (typeof apt.lat !== 'number' || typeof apt.lon !== 'number') return false;
    if (isNaN(apt.lat) || isNaN(apt.lon)) return false;
    if (!isFinite(apt.lat) || !isFinite(apt.lon)) return false;
    if (apt.lat < -90 || apt.lat > 90) return false;
    if (apt.lon < -180 || apt.lon > 180) return false;
    return true;
  });

  debug(`[TrailerMode] Generating ${TRAILER_FLIGHT_COUNT} flights from ${validAirports.length} airports`);

  for (let i = 0; i < TRAILER_FLIGHT_COUNT; i++) {
    // Pick random origin and destination (ensure they're different)
    let originCode, destCode;
    do {
      originCode = randomItem(validAirports);
      destCode = randomItem(validAirports);
    } while (originCode === destCode);

    const origin = AIRPORTS[originCode];
    const destination = AIRPORTS[destCode];

    // Calculate route distance
    const distance = calculateDistanceNm(origin, destination);

    // Skip very short routes (< 50nm)
    if (distance < 50) {
      i--;
      continue;
    }

    // Pick random aircraft type
    const aircraftType = randomItem(TRAILER_AIRCRAFT);
    let aircraft = AIRCRAFT_DATABASE[aircraftType];

    // Fallback if aircraft type not found
    if (!aircraft) {
      console.warn(`[TrailerMode] Aircraft type "${aircraftType}" not found, using E175`);
      aircraft = AIRCRAFT_DATABASE['E175'];
    }
    if (!aircraft) {
      console.error('[TrailerMode] E175 fallback also not found! Using defaults.');
      aircraft = { cruiseSpeed: 450, capacity: 76 };
    }

    // Pick random progress (spread across the distribution)
    const progress = PROGRESS_DISTRIBUTION[i % PROGRESS_DISTRIBUTION.length] +
                     (Math.random() * 0.03 - 0.015); // Add small variance

    // Clamp progress
    const clampedProgress = Math.max(0.05, Math.min(0.95, progress));

    // Calculate current position based on progress
    const currentLat = origin.lat + (destination.lat - origin.lat) * clampedProgress;
    const currentLon = origin.lon + (destination.lon - origin.lon) * clampedProgress;

    // Validate calculated position
    if (isNaN(currentLat) || isNaN(currentLon) || !isFinite(currentLat) || !isFinite(currentLon)) {
      console.warn(`[TrailerMode] Invalid position for flight ${i}, skipping`);
      i--;
      continue;
    }

    // Determine phase from progress
    const phase = getPhaseFromProgress(clampedProgress);

    // Calculate flight time (hours)
    const cruiseSpeed = aircraft.cruiseSpeed || 450;
    const flightTimeHours = distance / cruiseSpeed;

    // Calculate scheduled departure (in the past based on progress)
    const elapsedMs = flightTimeHours * 3600000 * clampedProgress;
    const scheduledDeparture = new Date(gameTime.getTime() - elapsedMs);

    // Create flight object
    const flight = {
      id: `trailer-${i}-${Date.now()}`,
      flightNumber: generateFlightNumber(i),
      origin: originCode,
      destination: destCode,
      aircraftType,
      aircraftId: `trailer-aircraft-${i}`,
      aircraftRegistration: `N${100 + i}TM`,

      // Timing
      scheduledDeparture: scheduledDeparture.toISOString(),
      scheduledArrival: new Date(scheduledDeparture.getTime() + flightTimeHours * 3600000).toISOString(),

      // State
      status: 'in_flight',
      phase,
      hasLaunched: true,

      // Progress (this is what the animation uses)
      progress: clampedProgress,
      distanceFraction: clampedProgress,
      distanceTraveled: distance * clampedProgress,
      overallProgress: clampedProgress,

      // Position
      currentLat,
      currentLon,

      // Route info
      distance: Math.round(distance),

      // Passengers/revenue (not important for trailer)
      passengers: Math.floor(aircraft.capacity * (0.7 + Math.random() * 0.25)),
      estimatedRevenue: Math.floor(distance * 8 + Math.random() * 5000),

      // No delays for trailer
      totalDelayMinutes: 0,
      delays: [],

      // Trailer flag
      isTrailerFlight: true,
    };

    flights.push(flight);
  }

  debug(`[TrailerMode] Generated ${flights.length} trailer flights`);

  // Log distribution of phases
  const phaseCounts = {};
  flights.forEach(f => {
    phaseCounts[f.phase] = (phaseCounts[f.phase] || 0) + 1;
  });
  debug('[TrailerMode] Phase distribution:', phaseCounts);

  return flights;
}

/**
 * Check if trailer mode is enabled
 */
export function isTrailerModeEnabled() {
  return TRAILER_MODE;
}
